import {objectFromEntries} from '@benchling/js-utils';

import {DNAAlignmentJSONAttributes} from 'constants/AlignmentConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import type {ContainerEditsResult} from 'molbio/dao/SequenceDAO';
import {StepItemStatus} from 'ui-library/StepNav';

export enum EditOpType {
  INSERT = 'INSERT',
  DELETE = 'DELETE',
  REPLACE = 'REPLACE',
}

export interface InsertOp {
  editType: EditOpType.INSERT;
  position: number;
  residues: string;
}

export interface DeleteOp {
  editType: EditOpType.DELETE;
  start: number;
  end: number;
}

export interface ReplaceOp {
  editType: EditOpType.REPLACE;
  start: number;
  end: number;
  residues: string;
}

export type EditOp = InsertOp | DeleteOp | ReplaceOp;

export interface DiffDetails {
  ops: Array<EditOp>;
  templateAlignmentRow: DNAAlignmentJSONAttributes;
  replacementAlignmentRow: DNAAlignmentJSONAttributes;
}

export interface EditEffects {
  [ReplaceWarningInfoKey.OVERLAPPING_LINKS]: number;
  [ReplaceWarningInfoKey.REMOVED_FEATURES]: number;
  [ReplaceWarningInfoKey.MUTATED_FEATURES]: number;
  // TODO: add keys for bocking issues (range edit affects mods, missing permissions, nested links)
}

export interface ReplaceLinkModalReducerState {
  currentStepKey: ReplaceLinkStepKey;
  sidebarExpanded: boolean;
  statusByStep: {[key in ReplaceLinkStepKey]: StepItemStatus | null};
  selectedContainerPolymerIds: Array<string>;
  confirmedContainerIds: Array<string>;
  containerEditResultsByPolymerId: Record<string, ContainerEditsResult>;
  isLoadingEditEffects: boolean;
  diffDetails: DiffDetails | null;
}

export enum ReplaceLinkStepKey {
  REVIEW_EDITS = 'REVIEW_EDITS',
  SELECT_SEQUENCES = 'SELECT_SEQUENCES',
  CONFIRM = 'CONFIRM',
}

export const ORDERED_STEPS: Array<ReplaceLinkStepKey> = [
  ReplaceLinkStepKey.REVIEW_EDITS,
  ReplaceLinkStepKey.SELECT_SEQUENCES,
  ReplaceLinkStepKey.CONFIRM,
];

export const UNVISITED_STEPS_STATUSES: {[key in ReplaceLinkStepKey]: StepItemStatus} = objectFromEntries(
  ORDERED_STEPS.map((step: ReplaceLinkStepKey) => [step, StepItemStatus.UNVISITED])
) as {[key in ReplaceLinkStepKey]: StepItemStatus};

export const INITIAL_REDUCER_STATE: ReplaceLinkModalReducerState = {
  currentStepKey: ReplaceLinkStepKey.REVIEW_EDITS,
  sidebarExpanded: true,
  statusByStep: UNVISITED_STEPS_STATUSES,
  selectedContainerPolymerIds: [],
  confirmedContainerIds: [],
  diffDetails: null,
  containerEditResultsByPolymerId: {},
  isLoadingEditEffects: false,
};

// Expose as function for testing.
export function getInitialReducerstate(): ReplaceLinkModalReducerState {
  return INITIAL_REDUCER_STATE;
}

// Oligos can be used as neither containers nor components in the current biointelligent links.
export type BiointelligentLinkPolymerSubtype = Exclude<
  PolymerSubtype,
  PolymerSubtype.dna_oligo | PolymerSubtype.rna_oligo
>;

// Translation links cannot be replaced right now, because the back-translation is not 1:1.
export type ReplaceableBiointelligentLinkType = Exclude<
  BiointelligentLinkType,
  BiointelligentLinkType.TRANSLATION
>;

export enum ReplaceWarningInfoKey {
  OVERLAPPING_LINKS = 'OVERLAPPING_LINKS',
  REMOVED_FEATURES = 'REMOVED_FEATURES',
  MUTATED_FEATURES = 'MUTATED_FEATURES',
}

export const LINK_TYPE_TO_CONTAINER_POLYMER_SUBTYPE = {
  [BiointelligentLinkType.DNA_PART]: PolymerSubtype.dna_sequence,
  [BiointelligentLinkType.RNA_PART]: PolymerSubtype.rna_sequence,
  [BiointelligentLinkType.TRANSCRIPTION]: PolymerSubtype.dna_sequence,
} as const;

export const LINK_TYPE_TO_COMPONENT_POLYMER_SUBTYPE = {
  [BiointelligentLinkType.DNA_PART]: PolymerSubtype.dna_sequence,
  [BiointelligentLinkType.RNA_PART]: PolymerSubtype.rna_sequence,
  [BiointelligentLinkType.TRANSCRIPTION]: PolymerSubtype.rna_sequence,
  [BiointelligentLinkType.AMINO_ACID_PART]: PolymerSubtype.aa_sequence,
} as const;
